<template>
  <b-container fluid>
    <b-row>
      <b-col cols="12">
        <!-- Mobile: stacked request cards (hidden on md+) -->
        <div class="d-md-none mobile-req-list">
          <div
            v-for="item in requests"
            :key="item.file_request_id"
            class="mobile-req-item"
          >
            <div class="d-flex align-items-start">
              <div class="flex-grow-1" style="min-width: 0">
                <div class="mobile-req__name">{{ requestName(item) }}</div>
                <div class="mobile-req__meta">
                  <span class="mobile-req__label">Due</span>
                  {{ item.file_request.due_date | formatDate }}
                </div>
              </div>
              <div class="mobile-req__right">
                <b-badge
                  pill
                  :variant="requestStatus(item).variant"
                  class="text-capitalize mobile-req__status"
                >
                  {{ requestStatus(item).label }}
                </b-badge>
                <b-button
                  v-if="canDeleteRequest"
                  variant="outline-light"
                  class="mobile-req__remove"
                  @click="removingFileRequest(true, item.file_request_id)"
                >
                  <b-icon-x-circle-fill variant="danger" />
                </b-button>
              </div>
            </div>
          </div>
          <h6 v-if="!requests || !requests.length" class="text-center py-4">
            Empty request file/s data
          </h6>
        </div>

        <!-- Desktop: full table (hidden below md) -->
        <div class="d-none d-md-block">
          <b-table
            responsive
            hover
            show-empty
            ref="recentReqFilesTable"
            :fields="fields"
            :items="requests"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <digger-loader />
              </div>
            </template>

            <template #head()="data">
              <span class="text-capitalize">{{ data.label }}</span>
            </template>

            <template #empty>
              <h6 class="text-center">Empty request file/s data</h6>
            </template>

            <template #emptyfiltered>
              <h6 class="text-center">No request file/s data found</h6>
            </template>

            <template
              #cell(name)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              {{
                data.item.file_share && data.item.file_share.file
                  ? data.item.file_share.file.name
                  : data.item.file_request.name
              }}
            </template>

            <template
              #cell(file_request.due_date)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              {{ data.item.file_request.due_date | formatDate }}
            </template>

            <template
              #cell(file_share.file.status)="data"
              :class="'px-0 text-center recent-req-file-action-col'"
            >
              <template
                v-if="data.item.file_share && data.item.file_share.file"
              >
                <b-badge
                  pill
                  :variant="statusBadge(data.item.file_share.file.status)"
                  class="badge-hovered text-capitalize"
                >
                  {{ data.item.file_share.file.status }}
                </b-badge>
              </template>
              <template
                v-else
                :class="'px-0 text-center recent-req-file-action-col'"
              >
                <b-badge
                  pill
                  variant="secondary"
                  class="badge-hovered text-capitalize"
                >
                  Not Started
                </b-badge>
              </template>
            </template>

            <template #cell(remove)="{ item }">
              <b-button
                variant="outline-light"
                style="height: 32px"
                class="px-2"
                @click="removingFileRequest(true, item.file_request_id)"
              >
                <b-icon-x-circle-fill variant="danger" />
              </b-button>
            </template>
          </b-table>
        </div>
      </b-col>

      <b-modal
        centered
        v-model="delModalShow"
        id="delete-request-modal"
        title="Delete file request?"
        footer-class="req-delete-footer"
      >
        <p class="my-4">Are you sure you want to delete the file request?</p>

        <template #modal-footer>
          <b-button
            variant="secondary"
            class="req-delete-btn"
            @click="removingFileRequest(false)"
          >
            No, keep it.
          </b-button>
          <b-button
            variant="primary"
            class="req-delete-btn"
            @click="deleteFileRequest()"
          >
            Yes, delete file request.
          </b-button>
        </template>
      </b-modal>
    </b-row>
  </b-container>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapGetters } from 'vuex'

export default {
  data() {
    return {
      delModalShow: false,
      fileRequestId: null,
    }
  },

  props: {
    requests: {
      default: [],
    },
  },

  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),

    isBusinessAdmin() {
      return this.user && this.user.user_type === 'business_admin'
    },

    isSupervisorTradie() {
      return !!(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },

    canDeleteRequest() {
      if (this.isSupervisorTradie) return false
      return !this.isBusinessAdmin || this.hasPermission('requests.delete')
    },

    fields() {
      const baseFields = [
        {
          key: 'name',
          label: 'File Name',
          class: 'w-25',
          sortable: true,
        },
        {
          key: 'file_request.due_date',
          label: 'Due Date',
          class: 'w-25',
          sortable: true,
        },
        {
          key: 'file_share.file.status',
          label: 'Status',
          class: 'w-25',
          sortable: true,
        },
      ]

      if (this.canDeleteRequest) {
        baseFields.push('remove')
      }

      return baseFields
    },
  },

  filters: {
    formatDate(date) {
      return dayjs(date).format('DD MMM YYYY')
    },
  },

  methods: {
    ...mapActions({
      deleteRequest: 'requests/deleteRequest',
    }),

    statusBadge(status) {
      return status === 'valid'
        ? 'success'
        : status === 'pending approval'
        ? 'warning'
        : status === 'rejected'
        ? 'danger'
        : 'secondary'
    },

    // Helpers for the compact mobile card layout (below md).
    requestName(item) {
      return item.file_share && item.file_share.file
        ? item.file_share.file.name
        : item.file_request.name
    },

    requestStatus(item) {
      if (item.file_share && item.file_share.file) {
        const status = item.file_share.file.status
        return { label: status, variant: this.statusBadge(status) }
      }
      return { label: 'Not Started', variant: 'secondary' }
    },

    removingFileRequest(status, fileRequestId) {
      if (status && !this.canDeleteRequest) return
      this.fileRequestId = fileRequestId
      this.delModalShow = status
    },

    async deleteFileRequest() {
      if (!this.canDeleteRequest) return
      await this.deleteRequest(this.fileRequestId).then(() => {
        this.$emit('onRequestDeleted', this.fileRequestId)
        this.removingFileRequest(false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
/* Mobile requested-files list: below md the table is replaced by compact
 * stacked cards so nothing scrolls sideways. Matches the mobile files list
 * on the tradie profile page. */
.mobile-req-list {
  border-top: 1px solid #e1e6ea;
}

.mobile-req-item {
  padding: 0.65rem 0.25rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
}

.mobile-req__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #364052;
  line-height: 1.3;
  word-break: break-word;
}

.mobile-req__meta {
  font-size: 0.75rem;
  color: #8b95a3;
  margin-top: 0.2rem;
  line-height: 1.5;
}

.mobile-req__label {
  font-weight: 600;
  color: #6c7581;
  margin-right: 0.15rem;
}

.mobile-req__right {
  flex-shrink: 0;
  margin-left: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
}

.mobile-req__status {
  font-weight: 600;
}

/* Borderless, compact red remove icon (no boxy outline button). */
.mobile-req__remove {
  height: 24px;
  width: 24px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  box-shadow: none;
  font-size: 1.1rem;
  line-height: 1;

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
}
</style>

<!-- Non-scoped: BootstrapVue renders modals at the end of <body>, outside this
     component's scope, so the delete-confirm modal must be styled globally.
     Keyed by the unique req-delete-* classes so nothing else is affected. -->
<style lang="scss">
/* Delete-confirmation modal: stack the actions full-width on phones so the
 * oversized global buttons don't crowd the small dialog. Desktop unchanged. */
@media (max-width: 575.98px) {
  .req-delete-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;

    .req-delete-btn {
      width: 100%;
      margin: 0;
      height: auto;
      padding: 0.65rem 1rem;
      font-size: 0.9rem;
    }
  }
}
</style>
